import { useMemo } from 'react';

import {
  CurrencyFormatter,
  roundNumber,
  useBetSlice,
  useChipSlice,
  useConfigSlice,
  useGameStateSlice,
  useSettingsSlice,
} from '@pp/core';

import { Bets } from '../../../config/bets';

export enum BlockReason {
  MaxLimit = 'MaxLimit',
  MinLimit = 'MinLimit',
  LowBalance = 'LowBalance',
}

export interface BetBlockedStatus {
  blocked: boolean;
  message?: string;
  attr?: string[];
  reason?: BlockReason;
}

export const useBetBlockedStatusConnected = (bet: Bets): BetBlockedStatus => {
  const selectedChip = useChipSlice.getSelectedChip();
  const balance = useGameStateSlice.getDeductibleBalance();
  const currency = useGameStateSlice.getCurrency();
  const currencyInfo = useGameStateSlice.getCurrencyInfo();
  const locale = useSettingsSlice.currentLanguage();

  const totalBet = useBetSlice.totalBet();
  const currentBets = useBetSlice.bets();
  const betLimits = useBetSlice.betLimits();
  const compliance = useConfigSlice.compliance();
  const hideCurrency = compliance?.hideCurrency;

  const getArr = (val: number) => {
    return [CurrencyFormatter.format(val, locale, { currency: currency!, hideCurrency: hideCurrency })]; // NOSONAR
  };

  return useMemo((): BetBlockedStatus => {
    if (!selectedChip) return { blocked: false };

    if (betLimits && currentBets) {
      const { min, max } = betLimits[bet];
      if (currentBets.betSpots[bet]?.value >= max) {
        return {
          blocked: true,
          message: 'CORE_MAXBETIS',
          attr: getArr(max),
          reason: BlockReason.MaxLimit,
        };
      }

      if (currentBets.betSpots[bet]?.value > 0 && currentBets.betSpots[bet]?.value < min) {
        return {
          blocked: true,
          message: 'CORE_MINBETIS',
          attr: getArr(min),
          reason: BlockReason.MinLimit,
        };
      }
    }

    if (balance < roundNumber(totalBet + selectedChip.value, currencyInfo?.digitsNumber || 0)) {
      return { blocked: true, message: 'INSUFFICIENTFUNDS', reason: BlockReason.LowBalance };
    }

    return { blocked: false };
  }, [selectedChip, betLimits, currentBets, balance, totalBet, currencyInfo, bet, locale, currency, hideCurrency]);
};
